import { useCallback, useEffect, useState } from 'react'

import { useAppSelector } from 'reduxTyped'
import { getIsUserFlagEnabled, getUserId } from 'common/core/selectors/appSelectors'
import { getInfoAboutService, initializeUsercentricsListeners } from 'utils/getInfoAboutService'
import { UserFlag } from 'utils/services/featureFlagsService/constants'

const SCRIPT_ID = 'intercomFinAIAgent'
const USERCENTRICS_INTERCOM_NAME = 'Intercom'
const INTERCOM_DOM_SELECTORS = [
  '#intercom-container',
  '#intercom-frame',
  '.intercom-lightweight-app',
]

const getHasIntercomConsent = () =>
  getInfoAboutService(USERCENTRICS_INTERCOM_NAME)[USERCENTRICS_INTERCOM_NAME]?.status ?? false

const useIntercomUserConsent = () => {
  const [isProvided, setIsProvided] = useState(getHasIntercomConsent)

  useEffect(() => {
    const updateConsent = () => setIsProvided(getHasIntercomConsent())
    const { unsubscribeListeners } = initializeUsercentricsListeners(updateConsent)
    updateConsent()
    return unsubscribeListeners
  }, [])

  return isProvided
}

export const useIsIntercomFinAIEnabled = () => {
  const hasIntercomConsent = useIntercomUserConsent()
  const isLoggedIn = useAppSelector((state) => getUserId(state) !== null)
  const isFinAIAgentDisabled = useAppSelector((state) =>
    getIsUserFlagEnabled(state, UserFlag.DISABLE_INTERCOM_FIN_AI_AGENT),
  )

  return isLoggedIn && hasIntercomConsent && !isFinAIAgentDisabled
}

export const shutdownIntercomFinAIAgent = () => {
  try {
    window.Intercom?.('shutdown')
  } catch (error) {
    console.error('Failed to shut down Intercom', error)
  }

  delete window.Intercom
  delete window.IntercomFinAIAgent
  delete window.intercomSettings

  document.getElementById(SCRIPT_ID)?.remove()
  INTERCOM_DOM_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => element.remove())
  })
}

export const useIntercomFinAIAgentWidget = () => {
  const isIntercomFinAIEnabled = useIsIntercomFinAIEnabled()
  const [isIntercomLoaded, setIsIntercomLoaded] = useState(false)

  const initializeIntercom = useCallback(() => {
    const intercomFunction = function () {
      intercomFunction.c(arguments)
    } as unknown as {
      (args: unknown): void
      q: unknown[]
      c: (args: unknown) => void
    }

    intercomFunction.q = []
    intercomFunction.c = function (args: unknown) {
      intercomFunction.q.push(args)
    }

    window.intercomSettings = window.intercomFinAISettings
    window.IntercomFinAIAgent = intercomFunction
    window.IntercomFinAIAgent('boot', window.intercomSettings)
    setIsIntercomLoaded(true)
  }, [])

  const loadScript = useCallback(() => {
    const appId = window?.intercomFinAISettings?.app_id
    if (!appId) {
      return
    }

    const existingScript = document.getElementById(SCRIPT_ID)

    if (existingScript) {
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.id = SCRIPT_ID
    script.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(script)

    script.onload = () => {
      initializeIntercom()
    }

    script.onerror = () => {
      setIsIntercomLoaded(false)
      console.error('Failed to load Intercom script')
    }
  }, [initializeIntercom])

  const updateIntercom = useCallback((intercomFinAIAgent: Window['IntercomFinAIAgent']) => {
    if (typeof intercomFinAIAgent !== 'function') {
      return false
    }

    window.intercomSettings = window.intercomFinAISettings
    intercomFinAIAgent('reattach_activator')
    intercomFinAIAgent('update', window.intercomSettings)

    return true
  }, [])

  const loadIntercomFinAIAgent = useCallback(() => {
    const intercomFinAIAgent = window.IntercomFinAIAgent
    if (updateIntercom(intercomFinAIAgent)) {
      return
    }
    loadScript()
  }, [loadScript, updateIntercom])

  useEffect(() => {
    if (!isIntercomFinAIEnabled) {
      return
    }

    const onLoad = () => loadIntercomFinAIAgent()

    if (document.readyState === 'complete') {
      loadIntercomFinAIAgent()
    } else {
      window.addEventListener('load', onLoad, { once: true })
    }

    return () => {
      window.removeEventListener('load', onLoad)
      shutdownIntercomFinAIAgent()
      setIsIntercomLoaded(false)
    }
  }, [isIntercomFinAIEnabled, loadIntercomFinAIAgent])

  return {
    isIntercomFinAIEnabled,
    isIntercomLoaded,
  }
}
